import { Customer, PaginatedResult, PaginationRequest, permissionAllowed } from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { IonCol, IonItem, useIonRouter } from "@ionic/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FilterList, { isFilterActive } from "../FilterList/FilterList";
import List from "../List/List";
import { defaultPageRange } from "../List/Pagination/pagerange";
import { useMyPermissions, useMyProfile } from "../Login/LoginProvider";
import Sidebar from "../Navigation/Sidebar/Customer";
import { useCustomer, useFetchCustomerList } from "../ReactData/customer";
import { useIsMobile } from "../Responsive";
import SettingsTextInput from "../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
//import { Filter } from "../System/Reports/types/reportTypes";
import { CustomersListProps } from "./type";

import Icon from "@ews/react-icons";
import { Link } from "react-router-dom";
import { countries, countryTranslationsKeyTemplate } from "../../setup/countries/countries";
import { globalDebounceTime, Locale } from "../../setup/locale";
import { FilterState } from "../FilterList/types";
import { useNamedRoutes } from "../NamedRoutes";
import { usePersistentState } from "../Persistence";
import SettingsSelect from "../Settings/SettingsComponents/SettingsSelect/SettingsSelect";
import SettingsSelectOption from "../Settings/SettingsComponents/SettingsSelectOption/SettingsSelectOption";
import { Filter } from "../System/Reports/types/reportTypes";
import style from './CustomerList.module.scss';
import { HeaderOption } from "../List/types";


type ListFilter = {
    name?: string;
    parentId?: string,
    customerNumber?: number | string;
    "details.zip"?: string;
    "details.city"?: string;
    "details.country"?: string;
};
type PersistentFilter = Filter<ListFilter> & FilterState;

//type PersistentFilterState = PaginationRequest & ListFilter & FilterState;

const standardFilter: PersistentFilter = {
    //     sortCriteria: "name",
    //     sortOrder: "ASC"
    pageSize: defaultPageRange
};

const onMenu = (id: string, onMenuToggle: () => void) =>
    <div style={{ height: "430px" }}>
        <Sidebar onMenuToggle={onMenuToggle} routeParams={{ id }} />
    </div>;

const ignoreKeys: (keyof PersistentFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];

const filterFields: (keyof ListFilter)[] = [
    "name", "customerNumber", "details.city", "details.country", "details.zip"
];

const link = { url: "customer", paramsKey: "id" };

const CustomerList: React.FC<CustomersListProps> = ({
    parentId,
    filter,
    onIsOpen,
    onFilterActive
}) =>
{

    const me = useMyProfile();
    const router = useIonRouter();

    const myPermissions = useMyPermissions();
    const allowedSettingsLink = permissionAllowed(myPermissions.createCustomer || 0);

    const _parentId = parentId || me.customerId;

    const parentCustomer = useCustomer(_parentId || "");

    const mobileView = useIsMobile();
    const { translate: t } = useL10n();
    const { generate } = useNamedRoutes();

    const header: HeaderOption<Locale, Customer>[] = useMemo(() => [
        {
            title: '',
            key: 'children',
            className: `${style.childIndicator} ${mobileView ? style.mobile : ''}`,
            child: (row) =>
            {
                if (!row.children) return <></>;

                const link = generate("customer:customers", { id: row.id });
                return <div onClick={(e) =>
                {
                    e.preventDefault(); router.push(link);
                }}> <Icon icon={"Arrow_right"} /></div >;
            },
            both: true,
        },
        {
            title: 'COM_FACPMANAGEMENT_CUSTOMERS_ID',
            key: 'customerNumber',
            className: style.customerNumber,
            both: true,
        },
        {
            title: 'COM_FACPMANAGEMENT_CUSTOMERS_NAME',
            key: 'name',
            className: style.customerName,
            both: true,

        },
        {
            title: 'COM_FACPMANAGEMENT_CUSTOMERS_NAME2',
            key: 'name2',
            className: style.name2,
            breakpoint: 1481, up: true,
            subEntity: 'details.name2'
        },
        {
            title: 'COM_FACPMANAGEMENT_CUSTOMERS_NAME3',
            key: 'name3',
            className: style.name3,
            breakpoint: 1681, up: true,
            subEntity: 'details.name3'
        },
        {
            title: 'COM_FACPMANAGEMENT_FACP_FIELD_FACP_ADDRESS1_LABEL',
            key: 'street',
            className: style.street,
            breakpoint: 992, up: true,
            subEntity: 'details.street'
        },
        {
            title: 'COM_FACPMANAGEMENT_CUSTOMERS_ADDRESS2',
            key: 'street2',
            className: style.street2,
            breakpoint: 1149, up: true,
            subEntity: 'details.street2'
        },
        {
            title: 'COM_FACPMANAGEMENT_CUSTOMERS_POSTAL_CODE',
            key: 'zip',
            className: style.zip,
            both: true,
            subEntity: 'details.zip'
        },
        {
            title: 'PLG_USER_PROFILE_FIELD_CITY_LABEL',
            key: 'city',
            className: style.city,
            both: true,
            subEntity: 'details.city'
        },
        {
            title: 'PLG_USER_PROFILE_FIELD_COUNTRY_LABEL',
            key: 'country',
            className: style.country,
            breakpoint: 1281, up: true,
            subEntity: 'details.country',
        },

        /*    {
               title: 'PLG_USER_PROFILE_FIELD_COUNTRY_LABEL',
               key: 'land',
               breakpoint: 1500, up: true,
                 }, */
        /*     {
                title: 'MOD_LATEST_CREATED',
                key: 'created',
                both: true,
                  }, */
    ], [generate, mobileView, router]);


    const [result, setResult] = useState<PaginatedResult<Customer>>({
        currentPage: 0,
        pageSize: standardFilter.pageSize || defaultPageRange,
        totalCountOfEntries: 0,
        totalCountOfPages: 0,
        results: [],
    });

    const fetchCustomers = useFetchCustomerList();

    const [currentState, setState] = usePersistentState<PersistentFilter>('customers:filter', standardFilter, parentId || "");

    const sortCriteria = useRef<{
        key: string;
        direction: "ASC" | "DESC";
    } | undefined>(undefined);

    const onPageChange = useCallback((page: PersistentFilter["page"]) => setState((priv) => ({ ...priv, page })), [setState]);

    const onSortChange = useCallback((sortCriteria: PersistentFilter["sortCriteria"], sortOrder: PersistentFilter["sortOrder"]) => setState((priv) => ({ ...priv, sortCriteria, sortOrder })), [setState]);
    const onPageSizeChange = useCallback((pageSize: PersistentFilter["pageSize"]) => setState((priv) => ({ ...priv, pageSize })), [setState]);
    const onReloadChange = useCallback(() => setState((priv) => ({ ...priv })), [setState]);

    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    const onIsDetailFilterOpen = useCallback((isDetailOpen: boolean) => onIsOpen && onIsOpen(isDetailOpen, "detail"), [onIsOpen]);
    const onResetFilter = useCallback(() =>
    {
        setState(() => standardFilter);
    }, [setState]);

    const isFilterChanged = isFilterActive<PersistentFilter>(currentState, standardFilter, ignoreKeys);

    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);


    const handleFilterResponse = useCallback(async (filterCriteria: PaginationRequest & ListFilter) =>
    {
        if (Object.keys((filterCriteria)).filter(f => filterFields.includes(f as keyof ListFilter)).length > 0) {

            if (_parentId && parentCustomer.tree === null) return;
            filterCriteria = { ...filterCriteria, tree: parentCustomer.tree || '' };

        } else {

            if (typeof _parentId === 'undefined') return;
            filterCriteria = { ...filterCriteria, parentId: _parentId || '' };

        }

        const response = (await fetchCustomers(filterCriteria));
        setResult(response);

    }, [parentCustomer.tree, fetchCustomers, _parentId]);

    useEffect(() =>
    {
        handleFilterResponse(currentState);
    }, [fetchCustomers, currentState, handleFilterResponse]);

    useEffect(() =>
    {
        sortCriteria.current =
            currentState.sortCriteria ? {
                key: currentState.sortCriteria,
                direction: currentState.sortOrder || "ASC"
            } : undefined;

    }, [currentState.sortCriteria, currentState.sortOrder]);


    return <div className={`${style.container}`}>
        <FilterList
            isOpen={filter?.isFilterOpen || false}
            isDetailsOpen={filter?.isDetailOpen || false}
            onIsOpen={onIsFilterOpen}
            onDetailOpen={onIsDetailFilterOpen}
            onReset={onResetFilter}>

            <IonCol>
                <SettingsTextInput
                    id="name"
                    text="COM_FACPMANAGEMENT_CUSTOMERS_NAME"
                    debounce={globalDebounceTime}
                    value={currentState.name}

                    onChange={(name) =>
                    {
                        const currentFilter = { ...currentState };

                        if (name.length) currentFilter.name = name;
                        else delete currentFilter.name;

                        setState(currentFilter);
                    }}
                />
            </IonCol>
            <IonCol>
                <SettingsTextInput
                    id="customerNumber"
                    text="COM_FACPMANAGEMENT_CUSTOMERS_ID"
                    debounce={globalDebounceTime}
                    value={currentState.customerNumber?.toString()}

                    onChange={(customerNumber) =>
                    {
                        const currentFilter = { ...currentState };

                        if (customerNumber.length) currentFilter.customerNumber = customerNumber;
                        else delete currentFilter.customerNumber;

                        setState(currentFilter);
                    }}
                />
            </IonCol>
            <IonCol>
                <SettingsTextInput
                    id="customerPostalCode"
                    text="COM_FACPMANAGEMENT_CUSTOMERS_POSTAL_CODE"
                    debounce={globalDebounceTime}
                    value={currentState["details.zip"]?.toString()}

                    onChange={(value) =>
                    {
                        const currentFilter = { ...currentState };

                        if (value.length) currentFilter["details.zip"] = value;
                        else delete currentFilter["details.zip"];

                        setState(currentFilter);
                    }}
                />
            </IonCol>
            <IonCol>
                <SettingsTextInput
                    id="customerCity"
                    text="PLG_USER_PROFILE_FIELD_CITY_LABEL"
                    debounce={globalDebounceTime}
                    value={currentState["details.city"]?.toString()}

                    onChange={(value) =>
                    {
                        const currentFilter = { ...currentState };

                        if (value.length) currentFilter["details.city"] = value;
                        else delete currentFilter["details.city"];

                        setState(currentFilter);
                    }}
                />
            </IonCol>
            <IonCol>
                <SettingsSelect
                    text={"PLG_USER_PROFILE_FIELD_COUNTRY_LABEL"}
                    className={style.CustomerListDropdownBox}
                    value={currentState["details.country"] || "Country"}
                    onSelect={(e) =>
                    {
                        const value = e;
                        const currentFilter = { ...currentState };

                        if (value?.length) {
                            currentFilter["details.country"] = value;
                        } else {
                            delete currentFilter["details.country"];
                        }
                        setState(currentFilter);
                    }}
                >
                    <SettingsSelectOption text={"-------" as Locale} value={""} />

                    {countries.map(country =>

                        <SettingsSelectOption text={t(countryTranslationsKeyTemplate(country.alpha3))} value={country.alpha3} key={country.alpha3} />
                    )}
                </SettingsSelect>
            </IonCol>
            <IonCol></IonCol>
            <IonCol></IonCol>
        </FilterList >

        {(_parentId && _parentId !== me.customerId) &&
            <>
                <IonItem style={{ minHeight: "var(--min-height)" }}>
                    <Link to={(parentCustomer.parentId && parentCustomer.parentId !== me.customerId) ? generate("customer:customers", { id: parentCustomer.parentId }) : generate("customers")}>
                        <Icon icon={"Arrow_left"} />
                        {t("[REACT_BACK]")}
                    </Link>&nbsp;|&nbsp;
                    <span>{[parentCustomer.name, parentCustomer.name2, parentCustomer.name3].join(", ")}</span>
                </IonItem>
            </>
        }

        <List
            rows={result.results}
            rowClass={`${style.row} ${mobileView ? style.mobile : ''}`}
            sort={sortCriteria.current}
            onSort={onSortChange}
            onPageChange={onPageChange}
            onPageSize={onPageSizeChange}
            onReload={onReloadChange}
            pageSize={result.pageSize}
            currentPage={result.currentPage}
            numberOfPages={result.totalCountOfPages}
            /* maxHeight={mobileView ? undefined : '50vh'} */
            noLink={!allowedSettingsLink}
            link={link}
            onMenu={onMenu}
            headers={header}

        />

    </div >;
};

export default CustomerList;
